import { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { Button } from '@mui/material';
import axios from 'axios';
import _ from 'lodash';

import ModalV2, {
    Button as ModalButton,
    ButtonKind,
    Footer,
    Status,
    StatusType,
} from '../shared/ModalV2';
import { getUserId, UserType } from './UserType';
import { Checkbox, Loading } from '../shared';

import { getPackDisplayName, handleError } from '../utils/utils';

import { Profile, Purchase } from '../types/accountTypes';
import { Pack } from '../types/packTypes';

import styles from './CopyProducts.module.scss';

const defaultProgressText = `Please note, this process can't be reversed`;

type AccountTypes = 'profile' | 'account';

type CopyProductsProps = {
    selectedProductIds: string[];
    allPacksInfo: Pack[];
    sourceUser: UserType;
};

const DEFAULT_ACCOUNT_TYPE: AccountTypes = 'profile';
const ACCOUNT_TYPE_OPTIONS: AccountTypes[] = ['profile', 'account'];
const ACCOUNT_TYPE_DISPLAY_NAMES: Record<AccountTypes, string> = {
    profile: 'Profile',
    account: 'Account',
};

const CopyProductsModal = (props: CopyProductsProps) => {
    const { sourceUser, selectedProductIds, allPacksInfo } = props;
    const [isModalShown, setIsModalShown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasCopiedSuccessfully, setHasCopiedSuccessfully] = useState(false);
    const [deviceIdCopyTo, setDeviceIdCopyTo] = useState('');
    const [profileIdCopyTo, setProfileIdCopyTo] = useState('');
    const [accountIdCopyTo, setAccountIdCopyTo] = useState('');
    const [progressText, setProgressText] = useState(defaultProgressText);
    const [productIdsToCopy, setProductIdsToCopy] = useState<string[]>([]);
    const [isProfileSearched, setIsProfileSearched] = useState(false);
    const [copyToAccountType, setCopyToAccountType] =
        useState<AccountTypes>(DEFAULT_ACCOUNT_TYPE);

    const updateCopyToAccountType = (value: AccountTypes) => {
        resetState();
        setCopyToAccountType(value);
    };

    const getPurchases = async (profileId: string) => {
        setIsLoading(true);
        setProfileIdCopyTo(profileId);
        setProductIdsToCopy([]);
        try {
            const res = await axios.get<{ purchases: Purchase[] }>(
                `/api/accounts/v2/purchases/${profileId}`,
            );
            const purchases = (res.data && res.data.purchases) || [];
            const filteredProducts = selectedProductIds.filter((productId) => {
                return !purchases.some((item) => item.productId === productId);
            });
            setProductIdsToCopy(filteredProducts);
        } catch (e) {
            handleError('Error fetching purchases', e);
        }
        setIsLoading(false);
    };

    const getPurchasesForAccount = async (accountId: string) => {
        setIsLoading(true);
        setAccountIdCopyTo(accountId);
        setProductIdsToCopy([]);
        try {
            const res = await axios.get<{ purchases: Purchase[] }>(
                `/api/accounts/v2/purchases/account/${accountId}`,
            );
            const purchases = (res.data && res.data.purchases) || [];
            const filteredProducts = selectedProductIds.filter((productId) => {
                return !purchases.some((item) => item.productId === productId);
            });
            setProductIdsToCopy(filteredProducts);
            setProgressText(
                `✅ Fetched purchases for account ID: ${accountId}`,
            );
        } catch (e) {
            handleError('Error fetching purchases', e);
        }
        setIsLoading(false);
    };

    const fetchProfiles = async () => {
        if (!deviceIdCopyTo) return;
        setIsLoading(true);
        setProgressText('Searching profiles');

        try {
            const res = await axios.get<Profile[]>(
                `/api/accounts/v2/profiles/${deviceIdCopyTo}`,
            );
            const profiles = res.data;

            if (!profiles || profiles.length === 0) {
                setIsLoading(false);
                setIsProfileSearched(true);
                setProfileIdCopyTo('');
                setProgressText('⚠️ No profiles found');
                return;
            }

            // ToDo do better handling of multiple profiles for MVP2
            getPurchases(profiles[0].profileId);
            setIsProfileSearched(true);
            setProgressText(
                `✅ Found profile with ID: ${profiles[0].profileId}`,
            );
        } catch (error) {
            handleError('Error fetching profiles', error);
            setProfileIdCopyTo('');
            setIsProfileSearched(true);
            setProgressText('⚠️ Error fetching profiles');
        }
        setIsLoading(false);
    };

    const fetchPurchases = async () => {
        if (!deviceIdCopyTo) return;
        setIsLoading(true);
        setProgressText('Getting purchases for account...');
        getPurchasesForAccount(deviceIdCopyTo);
    };

    const copyProducts = async () => {
        if (copyToAccountType === 'profile' && !profileIdCopyTo) return;
        if (copyToAccountType === 'account' && !accountIdCopyTo) return;

        if (productIdsToCopy.length === 0) {
            setProgressText('No products to copy');
            return;
        }
        setIsLoading(true);
        setProgressText('Copying products');

        const url =
            copyToAccountType === 'profile'
                ? `/api/accounts/v2/profile/${profileIdCopyTo}/addproducts`
                : `/api/accounts/v2/account/${accountIdCopyTo}/addproducts`;

        try {
            await axios.post(url, {
                productIds: productIdsToCopy,
            });
            setProgressText(
                `✅ ${productIdsToCopy.length} products successfully copied`,
            );
            setHasCopiedSuccessfully(true);
        } catch (error) {
            handleError('Error adding products', error);
            setProgressText(
                '⚠️ Error adding products. Please try again later.',
            );
            setHasCopiedSuccessfully(false);
        }
        setIsLoading(false);
    };

    const resetState = () => {
        setDeviceIdCopyTo('');
        setProfileIdCopyTo('');
        setAccountIdCopyTo('');
        setProductIdsToCopy([]);
        setProgressText(defaultProgressText);
        setIsLoading(false);
        setHasCopiedSuccessfully(false);
        setIsProfileSearched(false);
        setCopyToAccountType(DEFAULT_ACCOUNT_TYPE);
    };

    const renderCopyingDetails = () => {
        if (isLoading) return <Loading />;
        if (copyToAccountType === 'profile' && !profileIdCopyTo) return null;
        if (copyToAccountType === 'account' && !accountIdCopyTo) return null;

        return (
            <>
                <hr className={styles.lineSeparator} />
                {productIdsToCopy.length === 0 ? (
                    <p className="noMargin">
                        All selected products already exist in the target{' '}
                        {copyToAccountType}.
                    </p>
                ) : (
                    <>
                        <p className="noMargin">
                            Products selected for copy (
                            {productIdsToCopy.length}).
                        </p>
                        <label>
                            If a product already exists in the target{' '}
                            {copyToAccountType} it won't be copied.
                        </label>
                    </>
                )}
                <div className={styles.packsList} style={{ marginTop: 20 }}>
                    {allPacksInfo
                        .filter((pack) =>
                            pack.priceLevels.some((product) =>
                                productIdsToCopy.includes(product.productId),
                            ),
                        )
                        .map((pack, i) => (
                            <div className={styles.pack} key={'pack_' + i}>
                                <Tooltip id={`copy-products-${pack.packId}`} />
                                <div
                                    data-tooltip-id={`copy-products-${pack.packId}`}
                                    data-tooltip-content={pack.packId}
                                    style={{ display: 'inline-block' }}
                                >
                                    <Checkbox
                                        key={pack.packId}
                                        disabled={true}
                                        label={`${getPackDisplayName(pack)} (${
                                            pack.worth
                                        })`}
                                        highlight={false}
                                        isSearching={false}
                                        checked={true}
                                        pack={pack}
                                        onToggle={_.noop}
                                    />
                                </div>
                            </div>
                        ))}
                </div>
            </>
        );
    };

    const renderModalContent = () => {
        if (hasCopiedSuccessfully) {
            return (
                <>
                    <Status
                        type={StatusType.Info}
                        header={`${productIdsToCopy.length} products successfully copied`}
                        body={`The products can be accessed by the destination ${copyToAccountType} ID. You can now close this window.`}
                    />
                    <Footer>
                        <ModalButton
                            kind={ButtonKind.SECONDARY}
                            onClick={() => {
                                setIsModalShown(false);
                                resetState();
                            }}
                        >
                            Close
                        </ModalButton>
                    </Footer>
                </>
            );
        }

        const isCopyDisabled =
            isLoading ||
            (copyToAccountType === 'profile' && !profileIdCopyTo) ||
            (copyToAccountType === 'account' && !accountIdCopyTo) ||
            !productIdsToCopy.length;
        return (
            <div>
                <form
                    id="copyToForm"
                    className={styles.twoCols}
                    style={{ marginBottom: 15 }}
                    onReset={() => resetState()}
                    onSubmit={(e) => {
                        e.preventDefault();
                        switch (copyToAccountType) {
                            case 'profile':
                                fetchProfiles();
                                break;
                            case 'account':
                                fetchPurchases();
                                break;
                            default:
                                break;
                        }
                    }}
                >
                    <div>
                        <span>{`From ${
                            sourceUser.type === 'profileId'
                                ? 'Profile'
                                : 'Account'
                        } ID:`}</span>
                        <span className={styles.greyColor}>
                            {getUserId(sourceUser)}
                        </span>
                    </div>

                    <div>
                        <span>Account Type:</span>
                        <select
                            id="accountType"
                            className={styles.accountTypeSelector}
                            value={copyToAccountType}
                            onChange={(e) =>
                                updateCopyToAccountType(
                                    e.target.value as 'profile' | 'account',
                                )
                            }
                        >
                            {ACCOUNT_TYPE_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                    {
                                        ACCOUNT_TYPE_DISPLAY_NAMES[
                                            option as keyof typeof ACCOUNT_TYPE_DISPLAY_NAMES
                                        ]
                                    }
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <span>{`To ${
                            copyToAccountType === 'profile'
                                ? 'Profile'
                                : 'Account'
                        } ID:`}</span>
                        <input
                            name="deviceIdCopyTo"
                            type="text"
                            autoFocus={true}
                            className="semiwide"
                            placeholder="ex: 84ddd9dd-f3af-46b1-8fac-8241ff507e38"
                            value={deviceIdCopyTo}
                            onChange={(e) => {
                                setDeviceIdCopyTo(e.target.value);
                            }}
                        />
                        <ModalButton type="submit" disabled={!deviceIdCopyTo}>
                            Search
                        </ModalButton>
                        <ModalButton kind={ButtonKind.SECONDARY} type="reset">
                            Reset form
                        </ModalButton>
                    </div>

                    {copyToAccountType === 'profile' && (
                        <div
                            style={{
                                color: profileIdCopyTo ? 'green' : 'red',
                                display:
                                    isProfileSearched && !isLoading
                                        ? 'block'
                                        : 'none',
                            }}
                        >
                            <span>
                                <input
                                    type="hidden"
                                    name="profileIdCopyTo"
                                    value={profileIdCopyTo}
                                />
                            </span>
                            <small>
                                {profileIdCopyTo
                                    ? '✅ Profile ID found'
                                    : '⚠️ Sorry, Profile ID not found'}
                            </small>
                        </div>
                    )}
                </form>
                {(profileIdCopyTo || accountIdCopyTo) && renderCopyingDetails()}
                <hr className={styles.lineSeparator} />
                <Footer>
                    <p>{progressText}</p>
                    <div>
                        <ModalButton
                            kind={ButtonKind.SECONDARY}
                            onClick={() => setIsModalShown(false)}
                        >
                            Cancel
                        </ModalButton>
                        <ModalButton
                            kind={ButtonKind.PRIMARY}
                            onClick={copyProducts}
                            disabled={isCopyDisabled}
                        >
                            Copy products
                        </ModalButton>
                    </div>
                </Footer>
            </div>
        );
    };

    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setIsModalShown(!isModalShown)}
                disabled={selectedProductIds.length === 0}
            >
                Copy products
            </Button>
            {isModalShown && (
                <ModalV2>
                    <h1>Copy Products</h1>
                    {renderModalContent()}
                </ModalV2>
            )}
        </>
    );
};

export default CopyProductsModal;
